import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKey, ShieldCheck, ArrowRight, Warning } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";

export default function ResetPassword() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tokenFromUrl = params.get("token");
  // 3 distinct flows the same page can serve:
  //   1) Token-based reset (no auth) — link from forgot-password email.
  //   2) Forced first-login reset (auth'd, must_reset_password=true).
  //   3) Self-service change-password (auth'd, optional).
  const isTokenFlow = !!tokenFromUrl;
  const isForced = !isTokenFlow && !!user?.must_reset_password;

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Branch labels — extracted to avoid the nested-ternary anti-pattern.
  let eyebrowLabel;
  let titleNode;
  if (isTokenFlow) {
    eyebrowLabel = "Reset link received";
    titleNode = <>Pick a <span className="italic font-light text-[var(--vs-secondary)]">new password</span>.</>;
  } else if (isForced) {
    eyebrowLabel = "First-login reset required";
    titleNode = <>Set a <span className="italic font-light text-[var(--vs-secondary)]">new password</span> to continue.</>;
  } else {
    eyebrowLabel = "Change password";
    titleNode = <>Choose a new password.</>;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { setErr("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      if (isTokenFlow) {
        await api.post("/auth/reset-password-token", { token: tokenFromUrl, new_password: newPw });
        toast.success("Password reset — please sign in with your new password");
        navigate("/login", { replace: true });
      } else {
        const body = { new_password: newPw };
        if (!isForced) body.current_password = currentPw;
        await api.post("/auth/change-password", body);
        const me = await api.get("/auth/me");
        setUser(me.data);
        toast.success("Password updated");
        navigate("/app", { replace: true });
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grain-bg grid grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col px-6 lg:px-16 py-10">
        <div className="flex items-center gap-2 w-fit">
          <div className="w-9 h-9 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] grid place-items-center font-display font-bold">VS</div>
          <div className="leading-tight">
            <div className="font-display font-semibold">Vidya Seva</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--vs-text-secondary)]">Set new password</div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="my-auto max-w-md w-full"
        >
          <div className="eyebrow mb-3">{eyebrowLabel}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            {titleNode}
          </h1>

          {isForced && (
            <div className="mt-4 p-3 rounded-md border border-[var(--vs-warning)]/30 bg-[var(--vs-warning)]/10 text-sm flex items-start gap-2">
              <Warning size={16} weight="duotone" className="text-[var(--vs-warning)] flex-shrink-0 mt-0.5" />
              <div>
                Your account was created with a temporary password. For security, please set a permanent password before using the platform.
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-form">
            {!isForced && !isTokenFlow && (
              <div>
                <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)] mb-1.5">Current password</label>
                <input
                  type="password"
                  required
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  data-testid="reset-current-pw"
                  className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)] mb-1.5">New password</label>
              <input
                type="password"
                required
                minLength={8}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="reset-new-pw"
                className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
                placeholder="Min 8 characters"
              />
            </div>

            <div>
              <label className="block text-xs font-mono tracking-widest uppercase text-[var(--vs-secondary)] mb-1.5">Confirm new password</label>
              <input
                type="password"
                required
                minLength={8}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                data-testid="reset-confirm-pw"
                className="w-full px-4 py-3 rounded-md border border-[var(--vs-border)] bg-white focus:outline-none focus:border-[var(--vs-secondary)] focus:ring-2 focus:ring-[var(--vs-secondary)]/20"
              />
            </div>

            {err && (
              <div data-testid="reset-error" className="text-sm text-[var(--vs-danger)] bg-[var(--vs-danger)]/10 border border-[var(--vs-danger)]/30 rounded-md p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="reset-submit"
              className="w-full px-6 py-3 rounded-md bg-[var(--vs-secondary)] text-[var(--vs-bg)] hover:bg-[var(--vs-secondary-hover)] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2 font-medium"
            >
              {submitting ? "Saving…" : "Save new password"}
              {!submitting && <ArrowRight size={16} />}
            </button>

            {isForced && (
              <button
                type="button"
                onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                data-testid="reset-cancel"
                className="w-full text-xs underline underline-offset-4 text-[var(--vs-text-secondary)] hover:text-[var(--vs-secondary)]"
              >
                Sign out instead
              </button>
            )}
          </form>
        </motion.div>
      </div>

      <div className="hidden lg:flex bg-[var(--vs-bg-inverse)] text-[var(--vs-text-inverse)] flex-col px-12 py-10">
        <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vs-primary)] mb-4">Why we ask for this</div>
        <h2 className="font-display text-2xl font-semibold tracking-tight leading-tight max-w-md">
          Temporary passwords are intended for one use only — set a permanent one to keep your account safe.
        </h2>
        <ul className="mt-8 space-y-3 text-sm text-white/75 max-w-md">
          <li className="flex items-start gap-2"><LockKey size={14} weight="fill" className="text-[var(--vs-primary)] mt-1 flex-shrink-0" /> Your new password is hashed with bcrypt (cost 12) before storage.</li>
          <li className="flex items-start gap-2"><ShieldCheck size={14} weight="fill" className="text-[var(--vs-primary)] mt-1 flex-shrink-0" /> Old temporary password is invalidated immediately after this change.</li>
          <li className="flex items-start gap-2"><ShieldCheck size={14} weight="fill" className="text-[var(--vs-primary)] mt-1 flex-shrink-0" /> Min 8 characters. Use a passphrase you'll remember.</li>
        </ul>
        <div className="mt-auto pt-8 text-xs font-mono text-white/50">
          AES-256-GCM at rest · TLS 1.2+ in transit
        </div>
      </div>
    </div>
  );
}
